<script setup lang="ts">
/**
 * The one place to change what is on screen.
 *
 * Replaces a pair of view tabs, a phone-only pane switcher and a separate
 * message toggle in the footer. They were three controls for one decision —
 * which of four places you are looking at — and only one of them was ever
 * visible on a narrow screen.
 *
 * Anchored, not teleported like the status menu: the topbar does not scroll,
 * so there is nothing here to clip it.
 */
import { onBeforeUnmount, ref } from "vue";
import { useBoard } from "../stores/board.js";
import { useViewport, VIEW_LABELS, type View } from "../stores/viewport.js";
import { useOfferedViews } from "../lib/views.js";
import { swallowPress } from "../lib/dismiss.js";

const board = useBoard();
const viewport = useViewport();

const open = ref(false);

const views = useOfferedViews();
const root = ref<HTMLElement | null>(null);

/** Unread messages, shown against the Messages entry and on the button itself
 *  — the badge has to be legible from whichever view you happen to be in. */
const unreadOn = (view: View): number => (view === "messages" ? board.unreadMessages : 0);

function toggle(): void {
  open.value = !open.value;
  if (open.value) {
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKey);
  } else {
    close();
  }
}

function close(): void {
  open.value = false;
  window.removeEventListener("pointerdown", onPointerDown, true);
  window.removeEventListener("keydown", onKey);
}

function onPointerDown(ev: PointerEvent): void {
  if (root.value?.contains(ev.target as Node)) return;
  close();
  swallowPress(ev);
}

function onKey(ev: KeyboardEvent): void {
  if (ev.key === "Escape") close();
}

function choose(view: View): void {
  viewport.view = view;
  close();
}

onBeforeUnmount(close);
</script>

<template>
  <div ref="root" class="viewmenu">
    <button
      class="viewmenu-btn"
      type="button"
      aria-haspopup="listbox"
      :aria-expanded="open"
      :title="`Showing ${VIEW_LABELS[viewport.view]} — click to change view`"
      @click="toggle"
    >
      {{ VIEW_LABELS[viewport.view] }}
      <span v-if="board.unreadMessages && viewport.view !== 'messages'" class="badge" aria-label="unread messages">
        {{ board.unreadMessages > 99 ? "99+" : board.unreadMessages }}
      </span>
      <svg viewBox="0 0 10 6" width="9" height="6" aria-hidden="true" class="viewmenu-caret">
        <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <ul v-if="open" class="viewmenu-list" role="listbox">
      <li
        v-for="v in views"
        :key="v"
        role="option"
        :aria-selected="v === viewport.view"
        :class="{ current: v === viewport.view }"
        @click="choose(v)"
      >
        {{ VIEW_LABELS[v] }}
        <span v-if="unreadOn(v)" class="badge">{{ unreadOn(v) > 99 ? "99+" : unreadOn(v) }}</span>
      </li>
    </ul>
  </div>
</template>
